import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { isOrgOwnerRole, resolveClientPmsRole } from '../utils/orgMemberRole';
import type { OrgMemberRole } from '../services/organization.service';

export interface UserPermissions {
  canCreate?: boolean;
  canRead?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  canManageUsers?: boolean;
  canManageProperties?: boolean;
  canManageTenants?: boolean;
  canManagePayments?: boolean;
  canAccessDashboard?: boolean;
  canAccessProperties?: boolean;
  canAccessTenants?: boolean;
  canAccessPayments?: boolean;
  canAccessReports?: boolean;
  canAccessSettings?: boolean;
  canAccessAnalytics?: boolean;
}

interface UserWithPermissions {
  id: string;
  email: string;
  role: string;
  effectivePmsRole?: string | null;
  permissions?: UserPermissions;
  [key: string]: any;
}

interface AuthMeResponse {
  user: UserWithPermissions;
  membership?: {
    organizationId: string;
    role: OrgMemberRole | string;
    status: string;
  } | null;
  message: string;
}

/**
 * Hook to get current user permissions.
 * Program 4b: OWNER membership (or effectivePmsRole LANDLORD) has full access.
 */
export const usePermissions = () => {
  const { data, isLoading, error } = useQuery<AuthMeResponse>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const response = await apiService.getCurrentUser();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  const permissions: UserPermissions = data?.user?.permissions || {};
  const user = data?.user;
  const membership = data?.membership ?? null;
  const pmsRole = resolveClientPmsRole({
    membershipRole: membership?.role,
    userRole: user?.role,
    effectivePmsRole: user?.effectivePmsRole,
  });
  const isOwner = isOrgOwnerRole({
    membershipRole: membership?.role,
    userRole: user?.role,
    effectivePmsRole: user?.effectivePmsRole,
  });

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: keyof UserPermissions): boolean => {
    if (isOwner) return true;
    return permissions[permission] === true;
  };

  /**
   * Check if user can access a specific page
   */
  const canAccess = (page: 'dashboard' | 'properties' | 'tenants' | 'payments' | 'reports' | 'settings' | 'analytics'): boolean => {
    if (isOwner) return true;

    const permissionMap: Record<string, keyof UserPermissions> = {
      dashboard: 'canAccessDashboard',
      properties: 'canAccessProperties',
      tenants: 'canAccessTenants',
      payments: 'canAccessPayments',
      reports: 'canAccessReports',
      settings: 'canAccessSettings',
      analytics: 'canAccessAnalytics',
    };

    const permission = permissionMap[page];
    return permission ? hasPermission(permission) : false;
  };

  return {
    permissions,
    user,
    membership,
    pmsRole,
    isOwner,
    hasPermission,
    canAccess,
    isLoading,
    error,
  };
};
